import { faCheckDouble } from "@fortawesome/free-solid-svg-icons/faCheckDouble";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import debounce from "lodash/debounce";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../../components/AtiraIcon";
import { AtiraSelect } from "../../../../components/AtiraSelect";
import { AtiraTabs } from "../../../../components/AtiraTabs";
import { Button } from "../../../../components/Button";
import { Clickable } from "../../../../components/Clickable";
import { Flex } from "../../../../components/Flex";
import { Info } from "../../../../components/Info";
import { Input } from "../../../../components/Input";
import { TagChip } from "../../../../components/TagChip";
import { TagList } from "../../../../components/TagList";
import { Text } from "../../../../components/Text";
import { EntryAxios } from "../../../../axios/entry/entry.axios";
import { IntegrationAxios } from "../../../../axios/integration/integration.axios";
import { WhatsappCampaignLimits } from "../../../../model/integration/types/whatsapp/WhatsappCampaignLimits";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { useAppSelector } from "../../../../redux/store";
import { tagSliceSelectors } from "../../../../redux/tag/tag.selector";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { Breakpoints } from "../../../../theme/Breakpoints";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { getEntryLabel } from "../../../../utils/entryUtil";
import { Checkbox } from "../../../../utils/local-antd";
import { whatsappChatTitle } from "../../utils/whatsapp-chat-title";
import { TestIDs } from "../../../../tests/types/testIds";
import { CampaignPick } from "./types/CampaignPick";
import { CampaignPickOption } from "./types/CampaignPickOption";
import { campaignWaIdFromPhone } from "./utils/campaignNumber";
import { addablePicks, isSamePick, toPick } from "./utils/campaignPicks";

const SEARCH_LIMIT = 20;

/** A tag is a group to send to, so all of it loads, up to what one campaign
 * can hold
 */
const limitFor = (tagIds: string[]) =>
  tagIds.length ? WhatsappCampaignLimits.MAX_RECIPIENTS : SEARCH_LIMIT;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${Spacing.xs};

  @media (max-width: ${Breakpoints.PHONE}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Results = styled(Grid)`
  align-content: start;
  max-height: 20rem;
  overflow-y: auto;
  padding-inline-end: ${Spacing.xs};
`;

const ResultRow = styled.label<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  padding: ${Spacing.s};
  border-radius: ${Rounded.md};
  border: 1px solid ${({ theme }) => theme.lightgray};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`;

const RowText = styled(Flex)`
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const Picked = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.s};
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.md};
  background-color: ${({ theme }) => theme.lightSub};
`;

const SearchRow = styled(Flex)`
  gap: ${Spacing.s};
  align-items: center;

  > :first-child {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: ${Breakpoints.PHONE}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TagsSelect = styled(AtiraSelect)`
  min-width: 12rem;
`;

const BulkRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.s};
  flex-wrap: wrap;
`;

type Props = {
  picks: CampaignPick[];
  onChange: (picks: CampaignPick[]) => void;
  /** How many more can be picked */
  room: number;
  /** Numbers the campaign already has */
  takenWaIds: string[];
};

const PickerSearch: React.FC<{
  kind: CampaignPick["kind"];
  search: (term: string, tagIds: string[]) => Promise<CampaignPickOption[]>;
  picks: CampaignPick[];
  onToggle: (option: CampaignPickOption) => void;
  onPickMany: (added: CampaignPick[]) => void;
  onUnpickMany: (removed: CampaignPick[]) => void;
  room: number;
}> = ({ kind, search, picks, onToggle, onPickMany, onUnpickMany, room }) => {
  const [term, setTerm] = useState("");
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [options, setOptions] = useState<CampaignPickOption[]>([]);
  const [loading, setLoading] = useState(false);
  const latestRef = useRef(0);

  const { t } = useTranslation();
  const tags = useAppSelector(tagSliceSelectors.selectTags);

  const load = useMemo(
    () =>
      debounce((value: string, tags: string[]) => {
        // A slower, older search must not replace the results of a newer one
        const request = ++latestRef.current;
        const isLatest = () => request === latestRef.current;

        setLoading(true);

        search(value, tags)
          .then((found) => isLatest() && setOptions(found))
          .catch(() => isLatest() && setOptions([]))
          .finally(() => isLatest() && setLoading(false));
      }, 400),
    [search],
  );

  useEffect(() => {
    load(term, tagIds);
    return () => load.cancel();
  }, [load, term, tagIds]);

  const isPicked = (option: CampaignPickOption) =>
    picks.some((pick) => isSamePick(pick, option));

  const pickedWaIds = new Set(
    picks.flatMap(({ waId }) => (waId ? [waId] : [])),
  );

  // One message per number: several entries often share one, and ticking the
  // others would silently do nothing
  const blockedKeyOf = (option: CampaignPickOption) =>
    option.blockedKey ||
    (!isPicked(option) && option.waId && pickedWaIds.has(option.waId)
      ? "integration.whatsapp.meta.campaign.picker.blocked.sameNumber"
      : undefined);

  const pickedShown = options.filter(isPicked);
  // Ignoring the room: past it, the hint below says how many will fit
  const pickable = addablePicks(options, picks, Infinity);
  const addable = pickable.slice(0, Math.max(room, 0));

  const tagOptions = useMemo(
    () => tags.map((tag) => ({ value: tag._id, label: tag.name })),
    [tags],
  );

  const renderTagOption = (option: { value?: unknown }) => {
    const tag = tags.find(({ _id }) => _id === option.value);

    return tag ? <TagChip tag={tag} /> : null;
  };

  return (
    <Flex flexDirection="column" gap="s">
      <SearchRow>
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={t(
            `integration.whatsapp.meta.campaign.picker.search.${kind}`,
          )}
          dataTestId={TestIDs.integration.whatsappMeta.campaign.editor.search}
        />

        {tags.length ? (
          <TagsSelect
            mode="multiple"
            value={tagIds}
            options={tagOptions}
            onChange={(value) => setTagIds(value as unknown as string[])}
            optionRender={renderTagOption}
            showSearch={{ optionFilterProp: "label" }}
            popupMatchSelectWidth={false}
            maxTagCount={0}
            maxTagPlaceholder={(selected) =>
              t("common.n-selected", {
                number: selected.length,
                count: selected.length,
              })
            }
            placeholder={t("integration.whatsapp.meta.campaign.picker.tags")}
            className={TestIDs.integration.whatsappMeta.campaign.editor.tags}
          />
        ) : null}
      </SearchRow>

      {options.length ? (
        <BulkRow>
          {addable.length ? (
            <Button
              icon={faCheckDouble}
              fontSize="s"
              onClick={() => onPickMany(addable)}
              dataTestId={
                TestIDs.integration.whatsappMeta.campaign.editor.selectAll
              }
            >
              {t("integration.whatsapp.meta.campaign.picker.selectAll")}
            </Button>
          ) : null}

          {pickedShown.length ? (
            <Button
              icon={faXmark}
              color="main"
              backgroundColor="transparent"
              fontSize="s"
              onClick={() => onUnpickMany(pickedShown)}
              dataTestId={
                TestIDs.integration.whatsappMeta.campaign.editor.unselectAll
              }
            >
              {t("integration.whatsapp.meta.campaign.picker.unselectAll")}
            </Button>
          ) : null}
        </BulkRow>
      ) : null}

      {pickable.length > addable.length ? (
        <Info
          color="warning"
          width="100%"
          info={t("integration.whatsapp.meta.campaign.picker.roomLeft", {
            count: Math.max(room, 0),
            max: WhatsappCampaignLimits.MAX_RECIPIENTS,
          })}
        />
      ) : null}

      <Results>
        {options.map((option) => {
          const picked = isPicked(option);
          const blockedKey = blockedKeyOf(option);
          const disabled = !picked && (!!blockedKey || room <= 0);

          return (
            <ResultRow
              key={option.id}
              $disabled={disabled}
              data-testid={TestIDs.integration.whatsappMeta.campaign.editor.pick(
                option.waId || option.id,
              )}
            >
              <Checkbox
                checked={picked}
                disabled={disabled}
                onChange={() => onToggle(option)}
              />

              <RowText>
                <Text fontSize="s" wordBreak="break-word">
                  {option.label}
                </Text>
                <Text fontSize="xs" color="darkerSub">
                  {blockedKey && option.waId
                    ? `+${option.waId} · ${t(blockedKey)}`
                    : blockedKey
                      ? t(blockedKey)
                      : option.waId
                        ? `+${option.waId}`
                        : ""}
                </Text>

                {option.tagIds?.length ? (
                  <TagList tagIds={option.tagIds} max={3} />
                ) : null}
              </RowText>
            </ResultRow>
          );
        })}
      </Results>

      {!loading && !options.length ? (
        <Text fontSize="s" color="darkerSub">
          {t("integration.whatsapp.meta.campaign.picker.empty")}
        </Text>
      ) : null}
    </Flex>
  );
};

/** Chats and entries to send a campaign to, searched and ticked off */
export const WACampaignRecipientsPicker: React.FC<Props> = ({
  picks,
  onChange,
  room,
  takenWaIds,
}) => {
  const { t } = useTranslation();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );

  const pickedWaIds = useMemo(
    () => new Set(picks.flatMap(({ waId }) => (waId ? [waId] : []))),
    [picks],
  );

  // Searching again only when the numbers change, not on every render of a
  // parent that hands over a fresh array each time
  const takenKey = takenWaIds.join(",");

  const blockedByNumber = (waId?: string) => {
    if (!waId) {
      return "integration.whatsapp.meta.campaign.picker.blocked.noNumber";
    }

    if (takenWaIds.includes(waId)) {
      return "integration.whatsapp.meta.campaign.picker.blocked.inCampaign";
    }

    return undefined;
  };

  const searchChats = useMemo(
    () => async (term: string, tagIds: string[]) => {
      const { chats } = await IntegrationAxios.getWhatsAppMetaChatsList({
        userId,
        integrationId: integration!._id,
        phoneNumberId: integration!.config.phoneNumberId,
        search: term.trim() || undefined,
        tagIds: tagIds.length ? tagIds : undefined,
        limit: limitFor(tagIds),
      });

      return chats.map<CampaignPickOption>((chat) => ({
        kind: "chat",
        id: chat._id,
        label: whatsappChatTitle(chat),
        waId: chat.waId,
        tagIds: chat.tagIds,
        blockedKey: chat.campaignOptOutAt
          ? "integration.whatsapp.meta.campaign.picker.blocked.optedOut"
          : blockedByNumber(chat.waId),
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, integration?._id, takenKey],
  );

  const searchEntries = useMemo(
    () => async (term: string, tagIds: string[]) => {
      const { data } = await EntryAxios.getLatestEntries({
        userId,
        keyword: term.trim() || undefined,
        tagIds: tagIds.length ? tagIds : undefined,
        meta: { page: 0, count: limitFor(tagIds) },
      });

      return data.map<CampaignPickOption>((entry) => {
        const waId = campaignWaIdFromPhone(entry.fields?.phone);

        return {
          kind: "entry",
          id: entry._id,
          label: getEntryLabel(entry),
          waId: waId || undefined,
          tagIds: entry.tagIds,
          blockedKey: entry.fields?.phone
            ? waId
              ? blockedByNumber(waId)
              : "integration.whatsapp.meta.campaign.picker.blocked.localNumber"
            : "integration.whatsapp.meta.campaign.picker.blocked.noNumber",
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, takenKey],
  );

  const onToggle = (option: CampaignPickOption) => {
    const picked = picks.some((pick) => isSamePick(pick, option));

    if (picked) {
      onChange(picks.filter((pick) => !isSamePick(pick, option)));
      return;
    }

    // The same person found twice, as a chat and as an entry, is sent to once
    if (option.waId && pickedWaIds.has(option.waId)) {
      return;
    }

    onChange([...picks, toPick(option)]);
  };

  const onPickMany = (added: CampaignPick[]) => onChange([...picks, ...added]);

  const onUnpickMany = (removed: CampaignPick[]) =>
    onChange(
      picks.filter((pick) => !removed.some((other) => isSamePick(other, pick))),
    );

  return (
    <Flex flexDirection="column" gap="m">
      <AtiraTabs
        items={[
          {
            key: "chat",
            label: t("integration.whatsapp.meta.campaign.picker.tab.chat"),
            children: (
              <PickerSearch
                kind="chat"
                search={searchChats}
                picks={picks}
                onToggle={onToggle}
                onPickMany={onPickMany}
                onUnpickMany={onUnpickMany}
                room={room}
              />
            ),
          },
          {
            key: "entry",
            label: t("integration.whatsapp.meta.campaign.picker.tab.entry"),
            children: (
              <PickerSearch
                kind="entry"
                search={searchEntries}
                picks={picks}
                onToggle={onToggle}
                onPickMany={onPickMany}
                onUnpickMany={onUnpickMany}
                room={room}
              />
            ),
          },
        ]}
      />

      {picks.length ? (
        <Flex flexDirection="column" gap="xs">
          <Text fontSize="s" fontWeight="bold">
            {t("integration.whatsapp.meta.campaign.picker.picked", {
              count: picks.length,
            })}
          </Text>

          <Grid>
            {picks.map((pick) => (
              <Picked key={`${pick.kind}:${pick.id}`}>
                <Flex flexDirection="column" style={{ minWidth: 0 }}>
                  <Text fontSize="s" wordBreak="break-word">
                    {pick.label}
                  </Text>
                  <Text fontSize="xs" color="darkerSub">
                    {pick.waId
                      ? `+${pick.waId}`
                      : t(
                          `integration.whatsapp.meta.campaign.picker.kind.${pick.kind}`,
                        )}
                  </Text>
                </Flex>

                <Clickable
                  onClick={() =>
                    onChange(
                      picks.filter(
                        (other) =>
                          !(other.kind === pick.kind && other.id === pick.id),
                      ),
                    )
                  }
                >
                  <AtiraIcon icon={faXmark} color="darkerSub" size="sm" />
                </Clickable>
              </Picked>
            ))}
          </Grid>
        </Flex>
      ) : null}
    </Flex>
  );
};
